<template>
  <div>
    <!-- Header con total destacado -->
    <div class="d-flex align-center justify-space-between mb-4 flex-wrap">
      <div>
        <h1 class="mb-0"><v-icon color="primary" large>mdi-currency-usd</v-icon> Reporte de Pagos</h1>
        <div class="text-caption grey--text">{{ periodoTexto }}</div>
      </div>
      <v-chip color="success" large label dark class="text-h5 font-weight-black px-5 elevation-3">
        {{ moneda }}{{ formatNum(totalPagos) }}
        <span class="text-caption ml-2 opacity-75">total</span>
      </v-chip>
    </div>

    <!-- Filtro de período -->
    <periodo-busqueda @buscar="onBuscar" class="mb-4" />

    <!-- KPI Cards por membresía -->
    <div v-if="pagos.length">
      <div class="text-overline grey--text mb-1">Por tipo de membresía</div>
      <v-row class="mb-3">
        <v-col
          v-for="t in totalesMembresias" :key="t.nombre"
          cols="6" sm="4" md="3"
        >
          <v-card rounded="xl" class="elevation-2 pa-3 text-center">
            <v-icon color="deep-orange" class="mb-1">mdi-wallet-membership</v-icon>
            <div class="text-subtitle-2 font-weight-bold text-truncate">{{ t.nombre }}</div>
            <div class="text-h6 font-weight-black deep-orange--text">{{ moneda }}{{ formatNum(t.total) }}</div>
            <div class="text-caption grey--text">{{ t.cantidad || '' }} pagos</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- KPI Cards por usuario/cobrador -->
      <div class="text-overline grey--text mb-1">Por usuario (cobrador)</div>
      <v-row class="mb-4">
        <v-col
          v-for="u in totalesUsuarios" :key="u.nombre"
          cols="6" sm="4" md="3"
        >
          <v-card rounded="xl" class="elevation-2 pa-3 text-center">
            <v-icon color="green darken-2" class="mb-1">mdi-account-cash</v-icon>
            <div class="text-subtitle-2 font-weight-bold text-truncate">{{ u.nombre }}</div>
            <div class="text-h6 font-weight-black green--text darken-2">{{ moneda }}{{ formatNum(u.total) }}</div>
            <div class="text-caption grey--text">{{ u.cantidad || '' }} cobros</div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Tabla de pagos -->
    <v-card rounded="xl" class="elevation-4">
      <v-toolbar flat rounded="xl">
        <v-toolbar-title class="font-weight-bold">
          <v-icon left color="primary">mdi-table</v-icon>
          Detalle de pagos
        </v-toolbar-title>
        <v-spacer />
        <v-text-field
          v-model="busqueda"
          prepend-inner-icon="mdi-magnify"
          label="Buscar..."
          solo flat dense hide-details
          style="max-width:220px;"
          clearable
        />
      </v-toolbar>

      <v-data-table
        :loading="cargando"
        :headers="encabezadoTabla"
        :items="pagos"
        :search="busqueda"
        sort-by="fecha"
        sort-desc
        class="rounded-xl"
        :footer-props="{ itemsPerPageText: 'Por página', itemsPerPageOptions: [10, 25, 50, -1] }"
      >
        <!-- Imagen avatar -->
        <template v-slot:[`item.imagen`]="{ item }">
          <v-avatar size="36" class="my-1">
            <img :src="urlImagen(item.imagen)" />
          </v-avatar>
        </template>

        <!-- Monto con colores -->
        <template v-slot:[`item.monto`]="{ item }">
          <v-chip small color="success" dark label class="font-weight-bold">
            {{ moneda }}{{ formatNum(item.monto) }}
          </v-chip>
        </template>

        <!-- Membresía badge -->
        <template v-slot:[`item.membresia`]="{ item }">
          <v-chip small color="deep-orange" dark>{{ item.membresia }}</v-chip>
        </template>

        <!-- Cobrador -->
        <template v-slot:[`item.usuario`]="{ item }">
          <v-chip x-small color="blue-grey lighten-4" class="text-caption">
            <v-icon x-small left>mdi-account</v-icon>{{ item.usuario }}
          </v-chip>
        </template>

        <!-- Sin datos -->
        <template v-slot:no-data>
          <div class="text-center pa-6">
            <v-icon size="56" color="grey lighten-2">mdi-currency-usd-off</v-icon>
            <div class="grey--text mt-2">Sin pagos para el período seleccionado</div>
          </div>
        </template>
      </v-data-table>

      <!-- Totales al pie de la tabla -->
      <v-divider v-if="pagos.length" />
      <div v-if="pagos.length" class="d-flex justify-end pa-3 font-weight-bold text-h6">
        Total del período: &nbsp;
        <span class="success--text">{{ moneda }}{{ formatNum(totalPagos) }}</span>
      </div>
    </v-card>

    <!-- Resumen por miembro (top pagadores) -->
    <div v-if="totalesMiembros && totalesMiembros.length" class="mt-4">
      <div class="text-overline grey--text mb-2">Top miembros (por monto pagado)</div>
      <v-row>
        <v-col
          v-for="m in totalesMiembros.slice(0, 6)" :key="m.nombre"
          cols="12" sm="6" md="4"
        >
          <v-card rounded="xl" class="elevation-2">
            <v-list-item>
              <v-list-item-avatar size="40">
                <img :src="urlImagen(m.imagen)" />
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold text-truncate">{{ m.nombre }}</v-list-item-title>
                <v-list-item-subtitle>{{ m.matricula }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-chip small color="success" dark label>{{ moneda }}{{ formatNum(m.total) }}</v-chip>
            </v-list-item>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <v-overlay :value="cargando">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>
  </div>
</template>

<script>
import HttpService from '../../Servicios/HttpService'
import Utiles from '../../Servicios/Utiles'
import PeriodoBusqueda from '../Dialogos/PeriodoBusqueda.vue'

export default {
  name: 'Pagos',
  components: { PeriodoBusqueda },

  data: function() {
    return {
      busqueda: '',
      cargando: false,
      pagos: [],
      encabezadoTabla: [
        { text: '',              sortable: false, value: 'imagen',    width: 56 },
        { text: 'Miembro',       sortable: true,  value: 'nombre' },
        { text: 'Matrícula',     sortable: true,  value: 'matricula' },
        { text: 'Fecha',         sortable: true,  value: 'fecha' },
        { text: 'Monto',         sortable: true,  value: 'monto' },
        { text: 'Membresía',     sortable: true,  value: 'membresia' },
        { text: 'Cobró',         sortable: true,  value: 'usuario' },
      ],
      filtros: { fechaInicio: null, fechaFin: null },
      totalPagos: 0,
      totalesMembresias: [],
      totalesUsuarios: [],
      totalesMiembros: [],
      moneda: localStorage.getItem('monedaGimnasio') || '$',
    }
  },

  computed: {
    periodoTexto: function() {
      if (!this.filtros.fechaInicio) return 'Mostrando pagos de hoy'
      return 'Del ' + this.filtros.fechaInicio + ' al ' + this.filtros.fechaFin
    },
  },

  mounted: function() { this.obtenerPagos() },

  methods: {
    formatNum: function(n) {
      var num = parseFloat(n)
      if (isNaN(num)) return '0.00'
      return num.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    urlImagen: function(img) { return Utiles.generarURL(img) },

    onBuscar: function(fechas) {
      this.filtros = { fechaInicio: fechas[0], fechaFin: fechas[1] }
      this.obtenerPagos()
    },

    obtenerPagos: function() {
      var self = this
      this.cargando = true
      HttpService.obtenerConDatos({ metodo: 'obtener', filtros: this.filtros }, 'pagos.php')
      .then(function(respuesta) {
        if (respuesta) {
          self.pagos             = respuesta.pagos || []
          self.totalPagos        = respuesta.totalPagos || 0
          self.totalesMembresias = respuesta.totalesMembresias || []
          self.totalesUsuarios   = respuesta.totalesUsuario || []
          self.totalesMiembros   = respuesta.totalesMiembros || []
        }
        self.cargando = false
      })
    },
  },
}
</script>


// WEBPACK FOOTER //
// src/components/Pagos/Pagos.vue